import * as React from 'react';
import { useForm, useWatch } from 'react-hook-form';
import Input from '../../../../commonComponents/appForm/Input';
import { hasValue } from '../../../../lib/lib';
import DropDownListField from '../../../../commonComponents/appForm/DropDownListField';
import Entity from '../../../../models/Entity';
import { addOrUpdateClientEntity } from '../../../../core/api/publicClientApi';
import DateField from '../../../../commonComponents/appForm/DateField';
import CheckboxField from '../../../../commonComponents/fields/CheckboxField';
import { auStates, generateCountriesData, getEntityTypesData } from '../../userProfile/formFieldsSourceData';
import PublicForm from '../common/PublicForm';
import { StyledFormDiv, StyledLabel } from '../common/PublicLayout';
import { UserOfficeCountries } from '../../../../core/enums';

const PublicBusinessEntityForm = (props: Props) => {
	const { entity, onCancel, onSubmit } = props;

	const addNew = !hasValue(entity.entityId);
	const methods = useForm({ defaultValues: entity });
	const { signal } = new AbortController();
	const { control, getValues, setValue } = methods;
	const country = useWatch({ control, name: 'country', defaultValue: entity.country });

	const handleSubmit = async (newEntity: Entity) => {
		if (addNew) {
			const id = await addOrUpdateClientEntity(newEntity, signal);
			// eslint-disable-next-line no-param-reassign
			newEntity.entityId = id; // Need it so that entities grid item can have entityId
		} else {
			await addOrUpdateClientEntity(newEntity, signal);
		}

		onSubmit();
	};

	const handleCopyAddressCheckboxChange = (checked: boolean) => {
		if (checked) {
			const street = getValues('street');
			const suburb = getValues('suburb');
			const state = getValues('state');
			const postcode = getValues('postcode');
			setValue('postalStreet', street);
			setValue('postalSuburb', suburb);
			setValue('postalState', state);
			setValue('postalCountry', country);
			setValue('postalPostcode', postcode);
		}
	};

	return (
		<PublicForm
			methods={methods}
			showPrimaryButtonEndIcon
			onSubmit={handleSubmit}
			onBackButtonClick={onCancel}
			continueButtonText="Add business entity"
		>
			<StyledFormDiv>
				<StyledLabel>Business details</StyledLabel>
				<div />
			</StyledFormDiv>
			<StyledFormDiv>
				<Input label="Entity name" name="name" required />
				<Input label="Trading name (if different)" name="tradingName" />
			</StyledFormDiv>
			<StyledFormDiv>
				<DropDownListField label="Nature of business / type" name="type" data={getEntityTypesData()} required />
				<DateField label="Establishment date" name="establishmentDate" maxDate={new Date()} />
			</StyledFormDiv>
			<StyledFormDiv>
				<Input label="ABN" name="abn" type="tel" maxLength={20} />
				<Input label="ACN" name="acn" maxLength={20} />
			</StyledFormDiv>

			<StyledFormDiv>
				<StyledLabel>Street address</StyledLabel>
				<div />
			</StyledFormDiv>
			<StyledFormDiv>
				<Input label="Street" name="street" />
				<Input label="Suburb" name="suburb" maxLength={100} />
			</StyledFormDiv>
			<StyledFormDiv>
				{country === UserOfficeCountries.AU ? (
					<DropDownListField label="State" name="state" data={auStates} />
				) : (
					<Input label="State" name="state" maxLength={50} />
				)}
				<DropDownListField label="Country" name="country" data={generateCountriesData()} />
			</StyledFormDiv>
			<StyledFormDiv>
				<Input label="Postcode" name="postcode" maxLength={10} />
				<div />
			</StyledFormDiv>

			<StyledFormDiv>
				<StyledLabel>Postal address</StyledLabel>
				<div />
			</StyledFormDiv>
			<StyledFormDiv>
				<CheckboxField
					label="Copy from street address"
					name="copyAddress"
					onCheckboxChange={handleCopyAddressCheckboxChange}
				/>
				<div />
			</StyledFormDiv>
			<StyledFormDiv>
				<Input label="Street" name="postalStreet" />
				<Input label="Suburb" name="postalSuburb" maxLength={100} />
			</StyledFormDiv>
			<StyledFormDiv>
				{country === UserOfficeCountries.AU ? (
					<DropDownListField label="State" name="postalState" data={auStates} />
				) : (
					<Input label="State" name="postalState" maxLength={50} />
				)}
				<DropDownListField label="Country" name="postalCountry" data={generateCountriesData()} />
			</StyledFormDiv>
			<StyledFormDiv>
				<Input label="Postcode" name="postalPostcode" maxLength={10} />
				<div />
			</StyledFormDiv>

			<StyledFormDiv>
				<StyledLabel>Contact details</StyledLabel>
				<div />
			</StyledFormDiv>
			<StyledFormDiv>
				<Input label="Business phone" name="phone" type="tel" maxLength={20} />
				<Input label="Business fax" name="fax" maxLength={20} />
			</StyledFormDiv>
			<StyledFormDiv>
				<Input label="Business email" name="email" type="email" />
				<Input label="Website address" name="website" />
			</StyledFormDiv>
		</PublicForm>
	);
};

export default PublicBusinessEntityForm;

interface Props {
	entity: Entity;
	onCancel?: () => void;
	onSubmit: () => void;
}
